import { toaster } from "@/components/ui/toaster";
import { uploadFileToStorage } from "@/services/file.service";
import {
  CreateVoleoRequestBody,
  createVoleoRequest,
  getVoleoArchivoUploadUrl,
  getVoleoRequests,
} from "@/services/voleo-requests.service";
import { AppRoutes } from "@/constants/routes";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import { Dispatch, SetStateAction } from "react";
import { FieldValues, UseFormSetValue } from "react-hook-form";
import { NavigateFunction } from "react-router-dom";
import { VoleoRequestsListResponse } from "./voleo-request.schema";

export const VOLEO_REQUESTS_PAGE_SIZE = 10;

function getFileContentType(file: File): string {
  const ext = file.name.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "zip":
      return "application/zip";
    default:
      return file.type || "application/octet-stream";
  }
}

interface FetchVoleoRequestsHandlerProps {
  filters?: any;
  showLoad?: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setList: Dispatch<SetStateAction<VoleoRequestsListResponse | undefined>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchVoleoRequestsHandler = async ({
  filters,
  showLoad = true,
  setLoading,
  setList,
  navigate,
  fetchPermissions,
}: FetchVoleoRequestsHandlerProps) => {
  try {
    if (showLoad) setLoading(true);

    const response = await getVoleoRequests({
      CuentaOrigen: filters?.cuentaOrigen?.value
        ? Number(filters.cuentaOrigen.value)
        : undefined,
      Estados: filters?.Estados,
      FechaDesde: filters?.FechaDesde,
      FechaHasta: filters?.FechaHasta,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": filters?.["P.Records"] ?? VOLEO_REQUESTS_PAGE_SIZE,
    });

    setList(response ?? undefined);
  } catch (error) {
    const errorObj = error as AxiosError;

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          errorObj,
          "No se pudieron obtener las solicitudes de voleo.",
        ),
      });
    }

    throw error;
  } finally {
    if (showLoad) setLoading(false);
  }
};

interface SubmitVoleoFiltersHandlerProps {
  event: FieldValues;
  setPage: Dispatch<SetStateAction<number>>;
  fetchList: (filters?: any) => Promise<void>;
  setCurrentFilters: Dispatch<SetStateAction<any>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  filtersEmpty: boolean;
}

export const submitVoleoFiltersHandler = async ({
  event,
  setPage,
  fetchList,
  setCurrentFilters,
  setFiltersApplied,
  setOpenFilters,
  filtersEmpty,
}: SubmitVoleoFiltersHandlerProps) => {
  const estados = Array.isArray(event?.estados)
    ? event.estados.map(Number).filter((v) => !Number.isNaN(v))
    : undefined;

  const nextFilters = {
    cuentaOrigen: event?.cuentaOrigen || undefined,
    Estados: estados?.length ? estados : undefined,
    FechaDesde: event?.fechaDesde,
    FechaHasta: event?.fechaHasta,
    "P.Pagina": 1,
  };

  setPage(1);
  setCurrentFilters(nextFilters);
  setFiltersApplied(!filtersEmpty);
  setOpenFilters(false);

  await fetchList(nextFilters);
};

interface ClearVoleoFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setPage: Dispatch<SetStateAction<number>>;
  setCurrentFilters: Dispatch<SetStateAction<any>>;
  fetchList: (filters?: any) => Promise<void>;
}

export const clearVoleoFiltersHandler = async ({
  setValue,
  setFiltersApplied,
  setOpenFilters,
  setPage,
  setCurrentFilters,
  fetchList,
}: ClearVoleoFiltersHandlerProps) => {
  setValue("cuentaOrigen", undefined);
  setValue("estados", []);
  setValue("fechaDesde", undefined);
  setValue("fechaHasta", undefined);

  setFiltersApplied(false);
  setOpenFilters(false);
  setPage(1);
  setCurrentFilters(undefined);

  await fetchList({ "P.Pagina": 1 });
};

interface CreateVoleoRequestHandlerProps {
  values: any;
  file?: File;
  productos: { id: number; nombre: string }[];
  setLoadingCreate: (loading: boolean) => void;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const createVoleoRequestHandler = async ({
  values,
  file,
  productos,
  setLoadingCreate,
  navigate,
  fetchPermissions,
}: CreateVoleoRequestHandlerProps) => {
  const normalizeSingle = (v: any) => (Array.isArray(v) ? v?.[0] : v);
  const normalizeDecimal = (v: any) => Number(String(v ?? "").replace(",", "."));

  const cuentaOrigen = Number(normalizeSingle(values?.account?.value));
  if (!Number.isFinite(cuentaOrigen) || cuentaOrigen <= 0) {
    toaster.create({ type: "error", title: "Seleccioná una cuenta válida." });
    return;
  }

  const usuarioMobileRaw = values?.usuarioMobile?.value;
  const usuarioMobile = usuarioMobileRaw ? Number(usuarioMobileRaw) : undefined;

  const idEstablecimiento = Number(normalizeSingle(values?.procedencia));
  if (!Number.isFinite(idEstablecimiento) || idEstablecimiento <= 0) {
    toaster.create({ type: "error", title: "Seleccioná un establecimiento válido." });
    return;
  }

  const modalidad = Number(normalizeSingle(values?.modalidad));
  if (!Number.isFinite(modalidad) || modalidad <= 0) {
    toaster.create({ type: "error", title: "Seleccioná una modalidad válida." });
    return;
  }

  const tipoServicio = Number(normalizeSingle(values?.tipoServicio));
  if (!Number.isFinite(tipoServicio) || tipoServicio <= 0) {
    toaster.create({ type: "error", title: "Seleccioná un tipo de servicio válido." });
    return;
  }

  const idGranoProductivo = Number(normalizeSingle(values?.tipoCultivo));
  if (!Number.isFinite(idGranoProductivo) || idGranoProductivo <= 0) {
    toaster.create({ type: "error", title: "Seleccioná un tipo de cultivo válido." });
    return;
  }

  const productoValue = String(values?.producto ?? "").trim();
  if (!productoValue) {
    toaster.create({ type: "error", title: "Ingresá o seleccioná un producto." });
    return;
  }

  const matchedProducto = productos.find((p) => String(p.id) === productoValue);
  const idProducto = matchedProducto ? Number(matchedProducto.id) : undefined;
  const productoNombreLibre = matchedProducto ? undefined : productoValue;

  const dosisKgHa = normalizeDecimal(values?.dosis);
  if (!Number.isFinite(dosisKgHa) || dosisKgHa <= 0) {
    toaster.create({ type: "error", title: "Ingresá una dosis (kg/ha) válida." });
    return;
  }

  const hectareas = normalizeDecimal(values?.hectareas);
  if (!Number.isFinite(hectareas) || hectareas <= 0) {
    toaster.create({ type: "error", title: "Ingresá una cantidad de hectáreas válida." });
    return;
  }

  const telefonoContacto = String(values?.telefonoContacto ?? "").trim();
  if (!telefonoContacto) {
    toaster.create({ type: "error", title: "Ingresá un teléfono de contacto." });
    return;
  }

  if (!values?.fechaDeseada) {
    toaster.create({ type: "error", title: "Seleccioná una fecha deseada." });
    return;
  }
  const fechaDeseada = dayjs(values.fechaDeseada).toISOString();

  const observaciones = String(values?.observaciones ?? "").trim().slice(0, 50);

  const body: CreateVoleoRequestBody = {
    cuentaOrigen,
    ...(usuarioMobile ? { usuarioMobile } : {}),
    idEstablecimiento,
    modalidad,
    tipoServicio,
    idGranoProductivo,
    ...(idProducto ? { idProducto } : {}),
    ...(productoNombreLibre ? { productoNombreLibre } : {}),
    dosisKgHa,
    hectareas,
    telefonoContacto,
    fechaDeseada,
    observaciones,
  };

  setLoadingCreate(true);
  try {
    const idRaw = await createVoleoRequest(body);
    const id = Number(idRaw);
    const hasValidId = Number.isFinite(id) && id > 0;

    if (file) {
      if (!hasValidId) {
        toaster.create({
          type: "error",
          title:
            "La solicitud se creó pero no se pudo adjuntar el archivo: no se obtuvo el id de la solicitud.",
        });
        return;
      }

      try {
        const contentType = getFileContentType(file);
        const uploadData = await getVoleoArchivoUploadUrl(id, {
          tipoArchivo: contentType,
          nombreDocumento: file.name,
        });
        if (!uploadData) throw new Error();

        await uploadFileToStorage({
          uploadUrl: uploadData.uploadUrl,
          buffer: file,
          contentType,
        });
      } catch {
        toaster.create({
          type: "warning-strong",
          title:
            "La solicitud se creó, pero no se pudo subir el archivo. Podés volver a intentarlo desde el detalle.",
        });
        navigate(AppRoutes.VOLEO_REQUEST_DETAIL.replace(":id", String(id)));
        return;
      }
    }

    toaster.create({
      type: "success",
      title: "Solicitud creada exitosamente.",
    });

    navigate(AppRoutes.VOLEO_REQUESTS);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.VOLEO_REQUESTS,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(error, "No se pudo crear la solicitud."),
      });
    }

    throw error;
  } finally {
    setLoadingCreate(false);
  }
};
